import Head from 'next/head'

import type { FC, ReactNode } from 'react'
import type { SeoProps } from '@/types'

import { useRouter } from 'next/router'
import { siteConfig } from '@/constants/site'

const absolute = (origin: string, url: string | undefined): string | undefined => {
    if (!url) return undefined
    if (/^https?:\/\//.test(url)) return url
    if (!origin) return undefined

    return `${origin}${url.startsWith('/') ? url : `/${url}`}`
}

export const Seo: FC<SeoProps> = ({
    title,
    description = siteConfig.description,
    ogImage,
    siteName = siteConfig.name,
    origin = '',
    path,
    noindex,
    jsonLd
}): ReactNode => {
    const { locale, asPath } = useRouter()
    const pageTitle = title ? `${title} - ${siteName}` : siteName
    const cleanPath = (path ?? asPath.split('?')[0].split('#')[0]) || '/'
    const arabicUrl = origin ? `${origin}${cleanPath}` : undefined
    const englishUrl = origin ? `${origin}/en${cleanPath === '/' ? '' : cleanPath}` : undefined
    const canonical = locale === 'en' ? englishUrl : arabicUrl
    const image = absolute(origin, ogImage)

    return (
        <Head>
            <title>
                {pageTitle}
            </title>

            <meta name='description' content={description} />
            <meta property='og:title' content={pageTitle} />
            <meta property='og:description' content={description} />
            <meta property='og:type' content='website' />
            <meta property='og:site_name' content={siteName} />
            <meta property='og:locale' content={locale === 'en' ? 'en_US' : 'ar_EG'} />
            {canonical && <meta property='og:url' content={canonical} />}
            <meta name='twitter:card' content='summary_large_image' />
            <meta name='twitter:title' content={pageTitle} />
            <meta name='twitter:description' content={description} />

            {image && <meta property='og:image' content={image} />}
            {image && <meta name='twitter:image' content={image} />}

            {canonical && !noindex && <link rel='canonical' href={canonical} />}
            {arabicUrl && !noindex && <link rel='alternate' hrefLang='ar' href={arabicUrl} />}
            {englishUrl && !noindex && <link rel='alternate' hrefLang='en' href={englishUrl} />}
            {arabicUrl && !noindex && <link rel='alternate' hrefLang='x-default' href={arabicUrl} />}

            {noindex && <meta name='robots' content='noindex, nofollow' />}

            {jsonLd && (
                <script
                    type='application/ld+json'
                    dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, '\\u003c') }}
                />
            )}
        </Head>
    )
}
